import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link, Outlet, useLocation } from "react-router-dom";
import { Button } from "../ui/button";
import { toast } from "sonner";
import {
  PaintBucket,
  LayoutDashboard,
  FileText,
  MessageSquare,
  Mail,
  LogOut,
  Menu,
  X,
  User,
} from "lucide-react";
import { adminApi } from "../../lib/adminApi";

const navItems = [
  { name: "Dashboard", href: "/admin/dashboard", icon: LayoutDashboard },
  { name: "Quote Requests", href: "/admin/quotes", icon: FileText },
  { name: "Contact Messages", href: "/admin/contacts", icon: MessageSquare },
  { name: "Newsletter", href: "/admin/newsletter", icon: Mail },
];

export const AdminLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [adminName, setAdminName] = useState("");
  const [adminEmail, setAdminEmail] = useState("");

  const verifyAuth = useCallback(async () => {
    try {
      const res = await adminApi.get("/admin/me");
      setAdminName(res.data.name || "Admin");
      setAdminEmail(res.data.email || "");
    } catch {
      navigate("/admin/login");
    }
  }, [navigate]);

  useEffect(() => {
    verifyAuth();
  }, [verifyAuth]);

  const handleLogout = async () => {
    try {
      await adminApi.post("/admin/logout");
    } catch (error) {
    }
    toast.success("Logged out successfully");
    navigate("/admin/login");
  };

  const isActive = (href) => location.pathname === href;

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Mobile Sidebar Overlay */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 h-full w-64 bg-brand-navy z-50 transform transition-transform duration-300 lg:translate-x-0 ${
          isSidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        {/* Logo */}
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <Link to="/admin/dashboard" className="flex items-center gap-3">
            <div className="w-10 h-10 bg-brand-orange rounded-sm flex items-center justify-center">
              <PaintBucket className="w-6 h-6 text-white" />
            </div>
            <div>
              <span className="font-heading text-lg font-bold text-white uppercase">
                Roll Orlando
              </span>
              <span className="block text-[10px] text-white/50 tracking-widest uppercase">
                Admin Portal
              </span>
            </div>
          </Link>
          <button
            className="lg:hidden text-white/70 hover:text-white"
            onClick={() => setIsSidebarOpen(false)}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="p-4 space-y-2">
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <Link
                key={item.name}
                to={item.href}
                onClick={() => setIsSidebarOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-sm transition-colors ${
                  isActive(item.href)
                    ? "bg-brand-orange text-white"
                    : "text-white/70 hover:bg-white/10 hover:text-white"
                }`}
              >
                <Icon className="w-5 h-5" />
                <span className="font-medium">{item.name}</span>
              </Link>
            );
          })}
        </nav>

        {/* Back to Website */}
        <div className="absolute bottom-20 left-0 right-0 px-4">
          <Link
            to="/"
            className="flex items-center justify-center gap-2 px-4 py-3 text-white/50 hover:text-white transition-colors text-sm"
          >
            ← Back to Website
          </Link>
        </div>

        {/* User Info */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center">
              <User className="w-5 h-5 text-white/70" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{adminName}</p>
              <p className="text-xs text-white/50 truncate">{adminEmail}</p>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="lg:ml-64">
        {/* Top Bar */}
        <header className="bg-white border-b border-gray-200 sticky top-0 z-30">
          <div className="flex items-center justify-between px-6 py-4">
            <button
              className="lg:hidden text-gray-600 hover:text-gray-900"
              onClick={() => setIsSidebarOpen(true)}
            >
              <Menu className="w-6 h-6" />
            </button>

            <div className="flex-1 lg:flex-none" />

            <div className="flex items-center gap-4">
              <span className="hidden sm:block text-sm text-gray-600">
                {adminName}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={handleLogout}
                className="border-gray-300 text-gray-600 hover:bg-gray-100"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
};
